import { z } from 'zod';
import { COSMOS_CHAIN_SLUGS, EVM_CHAIN_SLUGS, NON_COSMOS_CHAIN_SLUGS, SOLANA_CHAIN_SLUGS, STARKNET_CHAIN_SLUGS, SUPPORTED_CHAIN_SLUGS, SUPPORTED_CHAIN_TYPES, } from '../constants/chains.js';
/** Use when a field should say which chain family it belongs to. */
export const supportedChainTypeSchema = z.enum(SUPPORTED_CHAIN_TYPES);
/** Use when a field should accept a public chain slug like ethereum or solana. */
export const supportedChainSlugSchema = z.enum(SUPPORTED_CHAIN_SLUGS);
/** Use when a field should only allow EVM chains. */
export const evmChainSlugSchema = z.enum(EVM_CHAIN_SLUGS);
/** Use when a field should only allow Cosmos SDK chains. */
export const cosmosChainSlugSchema = z.enum(COSMOS_CHAIN_SLUGS);
/** Use when a field should only allow Starknet chains. */
export const starknetChainSlugSchema = z.enum(STARKNET_CHAIN_SLUGS);
/** Use when a field should only allow Solana chains. */
export const solanaChainSlugSchema = z.enum(SOLANA_CHAIN_SLUGS);
/** Use when an existing generic endpoint should not accept Cosmos chains. */
export const nonCosmosChainSlugSchema = z.enum(NON_COSMOS_CHAIN_SLUGS);
/** Use for API timestamps in ISO format. */
export const isoTimestampSchema = z.iso.datetime();
/** Use when a field accepts a hex chain id like 0x1. */
export const hexQuantitySchema = z.string().regex(/^0x[0-9a-fA-F]+$/, 'Expected 0x-prefixed hexadecimal value');
/** Use for EVM account, token, or contract addresses. */
export const addressSchema = z.string().regex(/^0x[0-9a-fA-F]{40}$/, 'Expected 0x-prefixed EVM address');
/** Use when a wallet field may be EVM, Solana, Bitcoin, Starknet, or Cosmos style. */
export const genericAddressSchema = z.string().regex(/^(0x[0-9a-fA-F]{1,64}|[1-9A-HJ-NP-Za-km-z]{26,62}|[a-z][a-z0-9]{1,15}(valoper|valcons)?1[02-9ac-hj-np-z]{6,90})$/, 'Expected a supported wallet address format');
/** Use for EVM, Starknet, and Cosmos transaction hashes. */
export const txHashSchema = z.string().regex(/^(0x[0-9a-fA-F]{1,64}|[0-9A-Fa-f]{64})$/, 'Expected 0x-prefixed transaction hash');
/** Use for calldata, bytecode, or other hex payloads. */
export const hexDataSchema = z.string().regex(/^0x[0-9a-fA-F]*$/, 'Expected 0x-prefixed hexadecimal data');
/** Use for EVM event topics. */
export const evmTopicSchema = z.string().regex(/^0x[0-9a-fA-F]{64}$/, 'Expected 0x-prefixed 32-byte topic');
/** Use when pricing can be returned in different quote currencies. */
export const currencyCodeSchema = z.string().regex(/^[A-Za-z]{3,5}$/, 'Expected 3-5 letter currency code');
/** Use when a token is identified by symbol instead of address. */
export const symbolSchema = z.string().min(1, 'Expected non-empty token symbol');
/** Use when a field may accept an ENS name. */
export const ensNameSchema = z.string().refine((value) => value.includes('.'), {
    message: 'Expected ENS name like "vitalik.eth"',
});
/** Use for RPC-style block selectors like latest, 123, or 0x7b. */
export const blockTagSchema = z.union([
    z.literal('latest'),
    z.literal('earliest'),
    z.literal('pending'),
    z.number().int().nonnegative(),
    z.string().regex(/^\d+$/, 'Expected a decimal block number'),
    hexQuantitySchema,
]);
/** Use when a field can accept either a chain slug or a hex chain id. */
export const chainReferenceSchema = z.union([
    supportedChainSlugSchema,
    hexQuantitySchema,
]);
/** Use for ids, symbols, and other required strings shared across domains. */
export const nonEmptyStringSchema = z.string().min(1);
/** Use when an input may be passed as a number or numeric string shared across domains. */
export const numericLikeSchema = z.union([
    z.number(),
    z.string().regex(/^\d+(\.\d+)?$/, 'Expected a numeric value'),
]);
